import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";
import VisuallyHidden from "@atlaskit/visually-hidden";

import { wacToken } from "@/libraries/design/foundations";

import { ProjectAvatar } from "../../project-avatar";

import {
    tableBorderNotLastCellStyles,
    tableCellStyles
} from "./shared-styles.ts";
import { TableRow, type TableRowProps } from "./table-row";

const TABLE_HEADERS = ["Work", "Status", "Due date", "Priority", "Assignee"];

const tableStyles = css({
    tableLayout: "auto",
    borderRadius: "0.4rem",
    borderCollapse: "separate",
    borderSpacing: 0,
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderColor: "rgba(11, 18, 40, 0.14)",
    borderWidth: "1px",
    borderStyle: "solid",
    overflow: "hidden",
    width: "100%",
    maxWidth: "72.875rem",
    margin: "0 auto",
    background: wacToken("color.neutral.light0"),
    fontFamily: "var(--atlassian-sans-font-family)"
});

const tableCellHeaderStyles = css({
    backgroundColor: wacToken("color.neutral.light100"),
    fontWeight: 700,
    fontSize: "0.688rem",
    color: wacToken("color.neutral.light800"),
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderBlockEndColor: "rgba(11, 18, 40, 0.14)",
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "1px",
    textAlign: "start"
});

const teamTypeStyles = css({
    fontFamily: token("font.family.brand.heading"), // Charlie Display
    fontWeight: "bold",
    fontSize: "1.26rem",
    lineHeight: "2.1rem",
    color: wacToken("color.neutral.light1000"),
    display: "flex",
    alignItems: "center",
    columnGap: "0.63rem",
    width: "100%",
    maxWidth: "72.875rem",
    margin: "0 auto",
    marginBlockEnd: "1.375rem"
});

const teamTypeLoadingStyles = css({
    height: "0.5rem",
    backgroundImage:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "linear-gradient(to right, #1F69DA 7%, #C16CD4 29%, #F5A117 65%, #79AE44 85%)",
    opacity: 0.2,
    display: "inline-block",
    width: "8.375rem"
});

export interface PreviewTableProps {
    // Allow sparse/placeholder rows for progressive rendering.
    rows?: Array<Omit<TableRowProps, "isLastRow"> | undefined>;
    spaceName?: string;
}

export const PreviewTable = ({ rows, spaceName }: PreviewTableProps) => {
    const headerCells = TABLE_HEADERS.map((columnHeader) => {
        return (
            <th
                key={`column-header-${columnHeader}`}
                css={[
                    css(tableCellStyles),
                    tableCellHeaderStyles,
                    columnHeader !== "Assignee" &&
                        css(tableBorderNotLastCellStyles)
                ]}
            >
                {columnHeader}
            </th>
        );
    });

    const defaultRowCount = 6;
    const rowCount = rows?.length || defaultRowCount;
    const rowCells = Array.from({ length: rowCount }, (_, i) => {
        const isLastRow = i === rowCount - 1;

        if (rows && rows.length > 0 && rows[i]) {
            const {
                type,
                summary,
                status,
                dueDate,
                priority,
                assignee,
                assigneeImgSrc
            } = rows[i];
            // Ensure first row defaults to epic if type is missing/undefined (only for Kanban)
            return (
                <TableRow
                    type={type}
                    summary={summary}
                    status={status}
                    dueDate={dueDate}
                    priority={priority}
                    assignee={assignee}
                    assigneeImgSrc={assigneeImgSrc}
                    isLastRow={isLastRow}
                    key={"row-" + i}
                />
            );
        }

        return (
            <TableRow
                type={i === 0 ? "epic" : "task"}
                isLastRow={isLastRow}
                key={"row-" + i}
            />
        );
    });

    return (
        <>
            <h3 css={teamTypeStyles}>
                <ProjectAvatar />
                <span>
                    {spaceName ? (
                        spaceName
                    ) : (
                        <>
                            <VisuallyHidden>
                                Generating your Jira space
                            </VisuallyHidden>
                            <div
                                css={teamTypeLoadingStyles}
                                aria-hidden="true"
                            />
                        </>
                    )}
                </span>
            </h3>
            <table css={tableStyles}>
                <thead>
                    <tr>{headerCells}</tr>
                </thead>
                <tbody>{rowCells}</tbody>
            </table>
        </>
    );
};
